import type { CampaignDraftData } from "src/services/CampaignDraftTypes";
import type {
  WhatsAppGeneratedTemplateFields,
  WhatsAppMediaType,
  WhatsAppTemplateButtonPayload,
  WhatsAppCreateTemplateButton,
  WhatsAppCreateTemplatePayload,
  WhatsAppTemplateSavePayload,
} from "src/services/WhatsAppCampaignAPI";
import {
  BUTTON_DEFAULT_TEXT,
  BUTTON_TYPES,
  PHONE_NUMBER_PATTERN,
  SAMPLE_VALUES_BY_KEYWORD,
  TEMPLATE_FORM_LIMITS,
  TEMPLATE_LANGUAGES,
  TEMPLATE_NAME_PATTERN,
  TEMPLATE_PLACEHOLDER_PATTERN,
  WHATSAPP_EDITOR_KEY,
} from "./WhatsAppCampaignConstants";
import type {
  WhatsAppAiFilledField,
  WhatsAppButtonDraft,
  WhatsAppButtonType,
  WhatsAppMediaAttachment,
  WhatsAppTemplate,
  WhatsAppTemplateFormErrors,
  WhatsAppTemplateFormValues,
} from "./WhatsAppCampaignTypes";
import { toHttpUrl } from "./WhatsAppCampaignUtils";

const PLACEHOLDER_GLOBAL = new RegExp(TEMPLATE_PLACEHOLDER_PATTERN.source, "g");
const EDGE_PLACEHOLDER = /^\{\{\d+\}\}|\{\{\d+\}\}$/;
const ERRORS_KEY = `${WHATSAPP_EDITOR_KEY}.form.errors`;
const DEFAULT_COUNTRY_CODE = "+1";
const DEFAULT_ACTIVE_DAYS = 7;

let buttonDraftCount = 0;

export const createButtonDraft = (type: WhatsAppButtonType): WhatsAppButtonDraft => ({
  id: `${type}-${++buttonDraftCount}`,
  type,
  text: BUTTON_DEFAULT_TEXT[type],
  urlType: "static",
  url: "",
  trackConversation: false,
  offerCode: "",
  countryCode: DEFAULT_COUNTRY_CODE,
  phoneNumber: "",
  activeDays: DEFAULT_ACTIVE_DAYS,
});

export const isButtonType = (value: unknown): value is WhatsAppButtonType =>
  BUTTON_TYPES.some(type => type === value);

export const getAvailableButtonTypes = (
  buttons: WhatsAppButtonDraft[],
  keep?: WhatsAppButtonType
): WhatsAppButtonType[] =>
  BUTTON_TYPES.filter(type => type === keep || !buttons.some(button => button.type === type));

export const createInitialFormValues = (sendTo: string[]): WhatsAppTemplateFormValues => ({
  sendTo,
  name: "",
  language: "en",
  mediaType: null,
  headerText: "",
  body: "",
  footer: "",
  samples: {},
  buttons: [],
});

export const templateToFormValues = (
  template: WhatsAppTemplate,
  sendTo: string[]
): WhatsAppTemplateFormValues => ({
  ...createInitialFormValues(sendTo),
  name: template.name,
  language: template.language || "en",
  mediaType: template.mediaType,
  headerText: template.headerText ?? "",
  body: template.body,
  footer: template.footerText ?? "",
  buttons: template.buttons.flatMap((button): WhatsAppButtonDraft[] =>
    button.kind === "url_redirect"
      ? [{ ...createButtonDraft("visit_website"), text: button.text, url: button.url ?? "" }]
      : []
  ),
});

export const getBodyVariableIndexes = (body: string): number[] =>
  Array.from(new Set(Array.from(body.matchAll(PLACEHOLDER_GLOBAL), match => Number(match[1])))).sort(
    (first, second) => first - second
  );

export const getNextVariableIndex = (body: string): number =>
  Math.max(0, ...getBodyVariableIndexes(body)) + 1;

export const fillSamples = (text: string, samples: Record<string, string>): string =>
  text.replace(PLACEHOLDER_GLOBAL, (placeholder, index: string) => samples[index] || placeholder);

const getButtonError = (button: WhatsAppButtonDraft): string | null => {
  if (!button.text.trim()) {
    return `${ERRORS_KEY}.buttonText`;
  }
  switch (button.type) {
    case "visit_website":
      return toHttpUrl(button.url.trim()) ? null : `${ERRORS_KEY}.buttonUrl`;
    case "copy_code":
      return button.offerCode.trim() ? null : `${ERRORS_KEY}.offerCode`;
    case "call_phone":
      return PHONE_NUMBER_PATTERN.test(button.phoneNumber.trim())
        ? null
        : `${ERRORS_KEY}.phoneNumber`;
    default:
      return null;
  }
};

export const validateTemplateForm = (
  values: WhatsAppTemplateFormValues,
  hasMedia: boolean
): WhatsAppTemplateFormErrors => {
  const name = values.name.trim();
  const body = values.body.trim();
  const errors: WhatsAppTemplateFormErrors = {
    samples: getBodyVariableIndexes(values.body).filter(index => !values.samples[index]),
    buttons: {},
  };

  if (!values.sendTo.length) {
    errors.sendTo = `${ERRORS_KEY}.sendTo`;
  }
  if (!name) {
    errors.name = `${ERRORS_KEY}.nameRequired`;
  } else if (!TEMPLATE_NAME_PATTERN.test(name)) {
    errors.name = `${ERRORS_KEY}.nameFormat`;
  }
  if (!values.language) {
    errors.language = `${ERRORS_KEY}.language`;
  }
  if (!body) {
    errors.body = `${ERRORS_KEY}.bodyRequired`;
  } else if (EDGE_PLACEHOLDER.test(body)) {
    errors.body = `${ERRORS_KEY}.bodyEdgeVariable`;
  }
  if (values.mediaType && !hasMedia) {
    errors.media = `${ERRORS_KEY}.media`;
  }
  values.buttons.forEach(button => {
    const buttonError = getButtonError(button);
    if (buttonError) {
      errors.buttons[button.id] = buttonError;
    }
  });
  return errors;
};

export const hasFormErrors = (errors: WhatsAppTemplateFormErrors): boolean =>
  Boolean(errors.sendTo || errors.name || errors.language || errors.body || errors.media) ||
  errors.samples.length > 0 ||
  Object.keys(errors.buttons).length > 0;

const toButtonPayload = (button: WhatsAppButtonDraft): WhatsAppTemplateButtonPayload => {
  const base = { type: button.type, text: button.text.trim() };
  switch (button.type) {
    case "visit_website":
      return {
        ...base,
        url: button.url.trim(),
        url_type: button.urlType,
        track_conversation: button.trackConversation,
      };
    case "copy_code":
      return { ...base, offer_code: button.offerCode.trim() };
    case "call_phone":
      return { ...base, country_code: button.countryCode, phone_number: button.phoneNumber.trim() };
    default:
      return { ...base, active_days: button.activeDays };
  }
};

export const toSavePayload = (
  values: WhatsAppTemplateFormValues,
  attachment: WhatsAppMediaAttachment | null
): WhatsAppTemplateSavePayload => {
  const headerText = values.headerText.trim();
  const footer = values.footer.trim();
  return {
    name: values.name.trim(),
    language_code: values.language,
    list_names: values.sendTo,
    ...(values.mediaType &&
      attachment && {
        header_media: {
          type: attachment.uploaded.type,
          ref: attachment.uploaded.ref,
          mime_type: attachment.uploaded.mime_type,
          file_name: attachment.uploaded.file_name,
        },
      }),
    ...(!values.mediaType && headerText && { header_text: headerText }),
    body: values.body.trim(),
    ...(footer && { footer }),
    variable_samples: getBodyVariableIndexes(values.body).map(index => ({
      index,
      column: values.samples[index] ?? "",
    })),
    buttons: values.buttons.map(toButtonPayload),
  };
};

export const getSampleValue = (column: string): string => {
  const normalized = normalizeColumn(column);
  const match = SAMPLE_VALUES_BY_KEYWORD.find(([keyword]) => normalized.includes(keyword));
  return match?.[1] ?? column.replace(/_/g, " ");
};

const toCreateButton = (button: WhatsAppButtonDraft): WhatsAppCreateTemplateButton => {
  const text = button.text.trim();
  switch (button.type) {
    case "visit_website":
      return { type: "URL", text, url: button.url.trim() };
    case "call_phone":
      return {
        type: "PHONE_NUMBER",
        text,
        phone_number: `${button.countryCode}${button.phoneNumber.trim()}`,
      };
    case "copy_code":
      return { type: "COPY_CODE", example: [button.offerCode.trim()] };
    default:
      return { type: "VOICE_CALL", text };
  }
};

export const toCreatePayload = (
  values: WhatsAppTemplateFormValues,
  attachment: WhatsAppMediaAttachment | null
): WhatsAppCreateTemplatePayload => {
  const { header_text, header_media, footer } = toSavePayload(values, attachment);
  return {
    name: values.name.trim(),
    language_code: values.language,
    category: "MARKETING",
    ...(header_text && { header_text }),
    ...(header_media && { header_media }),
    body_text: values.body.trim(),
    ...(footer && { footer_text: footer }),
    variable_samples: Object.fromEntries(
      getBodyVariableIndexes(values.body).map(index => [
        `body_${index}`,
        getSampleValue(values.samples[index] ?? ""),
      ])
    ),
    buttons: values.buttons.map(toCreateButton),
  };
};

export const toDraftData = (
  values: WhatsAppTemplateFormValues,
  attachment: WhatsAppMediaAttachment | null
): CampaignDraftData => {
  const { name, list_names: sendTo, language_code: language, ...rest } = toSavePayload(
    values,
    attachment
  );
  return { campaign_name: name, source: "whatsapp", send_to: sendTo, language, ...rest };
};

export const wrapSelection = (
  value: string,
  start: number,
  end: number,
  marker: string
): { value: string; cursor: number } => ({
  value: `${value.slice(0, start)}${marker}${value.slice(start, end)}${marker}${value.slice(end)}`,
  cursor: end + marker.length * 2,
});

export const insertAt = (
  value: string,
  position: number,
  text: string
): { value: string; cursor: number } => ({
  value: `${value.slice(0, position)}${text}${value.slice(position)}`,
  cursor: position + text.length,
});

const fromButtonPayload = (payload: WhatsAppTemplateButtonPayload): WhatsAppButtonDraft => ({
  ...createButtonDraft(payload.type),
  text: payload.text || BUTTON_DEFAULT_TEXT[payload.type],
  urlType: payload.url_type ?? "static",
  url: payload.url ?? "",
  trackConversation: payload.track_conversation ?? false,
  offerCode: payload.offer_code ?? "",
  ...(payload.country_code && { countryCode: payload.country_code }),
  phoneNumber: payload.phone_number ?? "",
  ...(payload.active_days && { activeDays: payload.active_days }),
});

const MEDIA_TYPES: WhatsAppMediaType[] = ["image", "video", "document"];

export const applyGeneratedFields = (
  current: WhatsAppTemplateFormValues,
  fields: WhatsAppGeneratedTemplateFields,
  validListNames: string[]
): { values: WhatsAppTemplateFormValues; filled: WhatsAppAiFilledField[] } => {
  const values = { ...current };
  const filled: WhatsAppAiFilledField[] = [];
  const text = (value?: string | null) => value?.trim() || null;

  const sendTo = (fields.list_names ?? []).filter(name => validListNames.includes(name));
  if (sendTo.length) {
    values.sendTo = sendTo;
    filled.push("sendTo");
  }
  const name = text(fields.name);
  if (name) {
    values.name = name;
    filled.push("name");
  }
  if (TEMPLATE_LANGUAGES.some(language => language.value === fields.language_code)) {
    values.language = fields.language_code ?? current.language;
    filled.push("language");
  }
  const mediaType = MEDIA_TYPES.find(type => type === fields.media_type);
  if (mediaType) {
    values.mediaType = mediaType;
    filled.push("mediaType");
  }
  const headerText = text(fields.header_text);
  if (headerText && !mediaType) {
    values.headerText = headerText;
    filled.push("headerText");
  }
  const body = text(fields.body);
  if (body) {
    values.body = body;
    filled.push("body");
  }
  const footer = text(fields.footer);
  if (footer) {
    values.footer = footer;
    filled.push("footer");
  }
  if (fields.variable_samples?.length) {
    values.samples = Object.fromEntries(
      fields.variable_samples.map(sample => [sample.index, sample.column])
    );
  }
  if (fields.buttons?.length) {
    const seen = new Set<WhatsAppButtonType>();
    values.buttons = fields.buttons
      .filter(button => BUTTON_TYPES.includes(button.type) && !seen.has(button.type) && seen.add(button.type))
      .slice(0, TEMPLATE_FORM_LIMITS.maxButtons)
      .map(fromButtonPayload);
  }
  return { values, filled };
};

interface SuggestionRule {
  before?: RegExp;
  after?: RegExp;
  keywords: string[];
}

const CONTEXT_WINDOW = 30;
const SUGGESTION_RULES: SuggestionRule[] = [
  { before: /(hi|hello|hey|dear|welcome)[\s,!:-]*$/i, keywords: ["firstname", "lastname", "name"] },
  {
    before: /(discount|off|save|up to)[\s:-]*$/i,
    after: /^\s*%/,
    keywords: ["discount", "percent", "offer", "value"],
  },
  { before: /(\$|rs\.?|pkr|usd|aed|sar)\s*$/i, keywords: ["amount", "price", "total", "balance"] },
  { before: /(order|invoice|tracking|ref|id|#)[\s:#-]*$/i, keywords: ["order", "invoice", "tracking", "reference", "id"] },
  { before: /(code|coupon|promo)[\s:-]*$/i, keywords: ["code", "coupon", "promo"] },
  { before: /(on|by|until|before|date)[\s:-]*$/i, keywords: ["date", "expiry", "expire", "delivery"] },
];

const normalizeColumn = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, "");

export const getSuggestedColumns = (
  body: string,
  variableIndex: number,
  columns: string[]
): string[] => {
  const placeholder = `{{${variableIndex}}}`;
  const position = body.indexOf(placeholder);
  if (position < 0) {
    return [];
  }
  const before = body.slice(Math.max(0, position - CONTEXT_WINDOW), position);
  const after = body.slice(position + placeholder.length, position + placeholder.length + 4);
  const rule = SUGGESTION_RULES.find(
    ({ before: beforePattern, after: afterPattern }) =>
      Boolean(beforePattern?.test(before)) || Boolean(afterPattern?.test(after))
  );
  if (!rule) {
    return [];
  }
  const suggested: string[] = [];
  rule.keywords.forEach(keyword => {
    columns.forEach(column => {
      if (normalizeColumn(column).includes(keyword) && !suggested.includes(column)) {
        suggested.push(column);
      }
    });
  });
  return suggested;
};
